/**
 * Componente Footer
 * Pie de página con logo, redes sociales, enlaces legales y copyright
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../common/LocalizedLink';
import Logo from '../common/Logo';
import SocialMediaLinks from './SocialMediaLinks';
import useLocalizeContent from '../../hooks/useLocalizeContent';
import { socialLinks, legalLinks, elaboradoEnCanariasLink } from '../../data/siteData';

/**
 * Footer del sitio web
 * Incluye logo, redes sociales, enlaces legales y sello "Elaborado en Canarias".
 * Los textos se traducen con i18next y los enlaces internos conservan el idioma.
 * @returns {JSX.Element} Componente Footer
 */
const Footer = () => {
  const { t } = useTranslation();
  const { lc } = useLocalizeContent();

  return (
    <footer className="bg-intercasa-dark text-white py-2 sm:py-3 md:py-4 mt-8 sm:mt-12 md:mt-16">
      <div className="max-w-7xl mx-auto">
        {/* Sección principal del footer */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 xl:gap-5 mb-2 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex justify-center sm:justify-start items-center order-1">
            <Logo footer className="filter brightness-0 invert h-10 sm:h-12 lg:h-14 w-auto" />
          </div>

          {/* Redes Sociales */}
          <div className="flex flex-col items-center justify-center order-3 sm:order-2 sm:col-span-2 lg:col-span-1">
            <h3 className="font-lato font-semibold text-base sm:text-lg lg:text-xl mb-2">{t('footer.follow')}</h3>
            <SocialMediaLinks links={socialLinks} />
          </div>

          {/* Sello Elaborado en Canarias */}
          <div className="flex justify-center sm:justify-end items-center order-2 sm:order-3">
            <a
              href={elaboradoEnCanariasLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity duration-300"
            >
              <img 
                src="/images/footer/elaborado-en-canarias.webp" 
                alt={lc(elaboradoEnCanariasLink.alt)} 
                className="h-12 sm:h-14 lg:h-16 w-auto"
              />
            </a>
          </div>
        </div>

        {/* Separador */}
        <div className="border-t border-gray-600 my-2"></div>

        {/* Enlaces legales y copyright */}
        <div className="flex flex-col lg:flex-row justify-between items-center space-y-2 lg:space-y-0 px-4 sm:px-6 lg:px-8">
          {/* Enlaces legales */}
          <nav className="flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 lg:gap-8">
            {legalLinks.map((link) => (
              <LocalizedLink
                key={link.id}
                to={link.url}
                className="text-sm sm:text-base lg:text-lg font-lato text-gray-300 hover:text-intercasa-yellow transition-colors duration-300 whitespace-nowrap"
              >
                {t(link.labelKey)}
              </LocalizedLink>
            ))}
          </nav>

          {/* Copyright */}
          <p className="text-xs sm:text-sm lg:text-base font-lato text-gray-400 text-center lg:text-right whitespace-nowrap">
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
